import type { AnchorHTMLAttributes, ReactNode } from 'react'
import { trackWhatsAppClick } from '@/lib/analytics'

interface WhatsAppLinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> {
  /** URL do wa.me. Já vem montada por `whatsappHref`/`whatsappLink`. */
  href: string
  /** De onde saiu o clique — vira o parâmetro `source` do evento. */
  source: string
  /** "school" | "creator_page" | "bewave". */
  listingType?: string
  /** Slug do negócio, quando o clique acontece na página de um. */
  listingSlug?: string
  children: ReactNode
}

/**
 * Todo link de WhatsApp da área pública.
 *
 * É um COMPONENTE e não um hook de propósito: cada CTA aqui é uma âncora, e
 * um componente torna estruturalmente impossível acrescentar um link de
 * WhatsApp sem o evento junto. Com hook, bastaria alguém escrever `<a>` na
 * mão — que é exatamente como estavam todos antes.
 *
 * Só para CONTATO. Compartilhar por WhatsApp (`wa.me/?text=`) usa `<a>` comum:
 * é outra intenção, e contá-lo como conversão inflaria a métrica que o Ads
 * usa para otimizar.
 */
export default function WhatsAppLink({
  href,
  source,
  listingType,
  listingSlug,
  children,
  onClick,
  ...rest
}: WhatsAppLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        // Dispara e deixa a navegação seguir: o gtag envia por sendBeacon, que
        // sobrevive à troca de aba. Segurar o clique para "garantir" o envio
        // atrasaria a abertura do WhatsApp sem ganho real.
        trackWhatsAppClick({
          source,
          listing_type: listingType,
          listing_slug: listingSlug,
        })
        onClick?.(event)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
